import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Container, Image } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useGetUnrecognizedAnswersQuery } from "api/generated";
import { Loading } from "components";
import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import PageHeading from "components/PageHeading/PageHeading";
import Pagination from "components/Pagination/Pagination";
import { processApiError } from "utils/processApiError";

import UnrecognizedAnswerRow from "./UnrecognizedAnswerRow";
import UnrecognizedAnswerRowHead from "./UnrecognizedAnswerRowHead";
import UnrecognizedAnswersSummary from "./UnrecognizedAnswersSummary";

const PAGE_SIZE = 20;

const UnrecognizedAnswersPage = () => {
  const { t } = useTranslation(["unrecognizedAnswers", "common"]);
  const [page, setPage] = useState(1);
  const now = useMemo(() => new Date(), []);

  // AIDEV-NOTE: Jediný dotaz stránky. Položky, počty do karet i název úlohy vrací backend
  // najednou, druhý dotaz na úlohy sem nepatří. [TD-334]
  const {
    data,
    isLoading,
    error: apiError,
  } = useGetUnrecognizedAnswersQuery({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });

  useEffect(() => {
    if (apiError === undefined) return;
    processApiError(apiError);
  }, [apiError]);

  const answersWindow = data?.unrecognizedAnswers;
  const totalPages = answersWindow ? Math.ceil(answersWindow.totalCount / PAGE_SIZE) : 0;

  return (
    <>
      <div className="page-header">
        <Container className="container-mw-md page-container">
          <Breadcrumb>
            <li className="breadcrumb-item">
              <Link to="/">
                <IconArrowBack />
                {t("goHome", { ns: "common" })}
              </Link>
            </li>
          </Breadcrumb>
          <PageHeading className="mb-8px" />
        </Container>
      </div>

      <Container className="container-mw-md page-container">
        <div className="unrecognized-answers-intro">
          <p>
            <Trans i18nKey="intro.saved" ns="unrecognizedAnswers" components={{ i: <i />, br: <br /> }} />
          </p>
          <p>{t("intro.review")}</p>
          <p>{t("intro.abuse")}</p>
        </div>

        {isLoading ? (
          <Loading />
        ) : (
          <>
            {answersWindow && (
              <UnrecognizedAnswersSummary
                acceptedCount={answersWindow.acceptedCount}
                rejectedCount={answersWindow.rejectedCount}
              />
            )}

            {!answersWindow?.items.length ? (
              <div className="unrecognized-answers-empty">
                <Image src="/images/capy-thumbsup.svg" alt="" className="unrecognized-answers-empty__capybara" />
                <p className="unrecognized-answers-empty__title">{t("empty")}</p>
                <p className="unrecognized-answers-empty__hint">{t("emptyHint")}</p>
              </div>
            ) : (
              <>
                <div className="unrecognized-answers-list">
                  <UnrecognizedAnswerRowHead />
                  {answersWindow.items.map(item => (
                    <UnrecognizedAnswerRow key={item.id} item={item} now={now} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination current={page} total={totalPages} onNavigation={setPage} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default UnrecognizedAnswersPage;
